import { useMemo, useRef } from "react";
import { Text } from "@react-three/drei";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import type { CenterlineData } from "./useTunnelCenterline";

const INTERVAL = 50; // meters between markers

/** Text label that rotates only around Y axis to face camera */
function YBillboardText({ position, label }: { position: [number, number, number]; label: string }) {
  const groupRef = useRef<THREE.Group>(null);
  const { camera } = useThree();
  const worldPos = useMemo(() => new THREE.Vector3(), []);

  useFrame(() => {
    if (!groupRef.current) return;
    groupRef.current.getWorldPosition(worldPos);
    const dx = camera.position.x - worldPos.x;
    const dz = camera.position.z - worldPos.z;
    groupRef.current.rotation.y = Math.atan2(dx, dz);
  });

  return (
    <group ref={groupRef} position={position}>
      <Text
        position={[0, 2.5, 0]}
        fontSize={2.5}
        color="#ffffff"
        anchorX="center"
        anchorY="bottom"
        outlineWidth={0.1}
        outlineColor="#000000"
      >
        {label} m
      </Text>
    </group>
  );
}

export function StationMarkers({ data }: { data: CenterlineData }) {
  const markers = useMemo(() => {
    const result: { position: [number, number, number]; label: string }[] = [];
    const len = data.totalLength;

    for (let s = 0; s <= len; s += INTERVAL) {
      const t = s / len;
      const p = data.curve.getPointAt(t);
      result.push({
        position: [p.x, p.y + 3, p.z],
        label: `${s}`,
      });
    }
    return result;
  }, [data]);

  return (
    <group>
      {markers.map((m) => (
        <group key={m.label} position={m.position}>
          {/* Vertical tick line */}
          <mesh position={[0, -1.5, 0]}>
            <cylinderGeometry args={[0.1, 0.1, 3, 4]} />
            <meshStandardMaterial color="#e74c3c" />
          </mesh>
          {/* Label */}
          <YBillboardText position={[0, 0, 0]} label={m.label} />
        </group>
      ))}
    </group>
  );
}
